import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import * as productReleasesService from '../services/productReleases';
import * as auth from './auth/authSlice';
import { userConfigsSelectors } from './userConfigs';
import { entitiesAdapter } from './common/adapter';

const initialState = {
  isFetching: false,
  isCreating: false,
  isUpdating: false,
  fetchingError: null,
  creatingUpdatingError: null,
};

const sliceName = 'productReleases';

export const fetchAll = createAsyncThunk(
  `${sliceName}/fetchAll`,
  (args, thunkAPI) => {
    return productReleasesService.getProductReleases()
      .catch((error) => {
        error.clientMessage = "Can't get product releases from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const fetchById = createAsyncThunk(
  `${sliceName}/fetchById`,
  (args, thunkAPI) => {
    return productReleasesService.getProductReleaseById(args.id, auth.logout)
      .catch((error) => {
        error.clientMessage = "Can't get product releases from server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const create = createAsyncThunk(
  `${sliceName}/create`,
  (args, thunkAPI) => {
    console.log(args)
    return productReleasesService.addProductRelease(
      args.productName, args.productVersion, args.releaseDate
    )
      .catch((error) => {
        error.clientMessage = "Can't post product releases to server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const update = createAsyncThunk(
  `${sliceName}/update`,
  (args, thunkAPI) => {
    return productReleasesService.updateProductRelease(
      args.id, args.productName, args.productVersion, args.releaseDate
    )
      .catch((error) => {
        error.clientMessage = "Can't update product releases on server";
        return thunkAPI.rejectWithValue(error)
      });
  }
);

export const addToFavorites = createAsyncThunk(
  `${sliceName}/favoritesAdd`,
  (args) => {
    return productReleasesService.addProductReleaseToFavorites(args.id)
  }
);
export const removeFromFavorites = createAsyncThunk(
  `${sliceName}/favoritesRemove`,
  (args) => {
    return productReleasesService.removeProductReleaseFromFavorites(args.id)
  }
);

const productReleases = createSlice({
  name: sliceName,
  initialState: entitiesAdapter.getInitialState(initialState),
  reducers: {},
  extraReducers: {
    [fetchAll.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.setAll(state, action);
    },
    [fetchById.fulfilled]: (state, action) => {
      state.isFetching = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [create.fulfilled]: (state, action) => {
      state.isCreating = false;
      entitiesAdapter.addOne(state, action);
    },
    [update.fulfilled]: (state, action) => {
      state.isUpdating = false;
      entitiesAdapter.upsertOne(state, action);
    },
    [addToFavorites.fulfilled]: entitiesAdapter.upsertOne,
    [removeFromFavorites.fulfilled]: entitiesAdapter.upsertOne,

    [fetchAll.pending]: (state) => {
      state.isFetching = true;
      state.creatingUpdatingError = null;
      state.fetchingError = null;
    },
    [fetchById.pending]: (state) => {
      state.isFetching = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [create.pending]: (state) => {
      state.isCreating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },
    [update.pending]: (state) => {
      state.isUpdating = true;
      state.fetchingError = null;
      state.creatingUpdatingError = null;
    },

    [fetchAll.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [fetchById.rejected]: (state, action) => {
      state.isFetching = false;
      state.fetchingError = action.payload;
    },
    [create.rejected]: (state, action) => {
      state.isCreating = false;
      state.creatingUpdatingError = action.payload;
    },
    [update.rejected]: (state, action) => {
      state.isUpdating = false;
      state.creatingUpdatingError = action.payload;
    },
  }
});

const { reducer } = productReleases;

const productReleasesAdapterSelectors = entitiesAdapter.getSelectors((state) => state[sliceName]);

const selectedProductReleasesSelector = createSelector(
  (state) => userConfigsSelectors.selectedItems(state, sliceName),
  (state) => productReleasesAdapterSelectors.selectEntities(state),
  (selectedIds, entities) => selectedIds.map((id) => entities[id]),
)

export const productReleasesSelectors = {
  ...productReleasesAdapterSelectors,
  isFetching: (state) => state[sliceName].isFetching,
  isCreating: (state) => state[sliceName].isCreating,
  isUpdating: (state) => state[sliceName].isUpdating,

  fetchingError: (state) => state[sliceName].fetchingError,
  creatingUpdatingError: (state) => state[sliceName].creatingUpdatingError,

  selectedProductReleases: selectedProductReleasesSelector,
  state: (state) => state[sliceName],
};

export default reducer;



// WEBPACK FOOTER //
// ./src/store/productReleasesSlice.js